import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "@apollo/client";
import { GET_ANALYTICS } from "../../graphql/queries/analytics";
import { Loader } from "../../shared/Loader";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Brush,
} from "recharts";
import {
  AiFillPieChart,
  AiOutlineRise,
  AiOutlineTrophy,
  AiOutlineDollar,
  AiOutlineStar,
  AiOutlineCalendar,
} from "react-icons/ai";

const COLORS = [
  "#F7931A",
  "#2775CA",
  "#00C853",
  "#627EEA",
  "#FFBB28",
  "#FF8042",
];

const CurrencyToggle: React.FC<{
  currency: "NGN" | "USD" | "GHS";
  setCurrency: (currency: "NGN" | "USD" | "GHS") => void;
}> = ({ currency, setCurrency }) => (
  <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
    {(["NGN", "USD", "GHS"] as const).map((c) => (
      <button
        key={c}
        onClick={() => setCurrency(c)}
        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
          currency === c
            ? "bg-primary text-white shadow-lg"
            : "text-gray-400 hover:text-white"
        }`}
      >
        {c}
      </button>
    ))}
  </div>
);

const PeriodFilter: React.FC<{
  period: string;
  setPeriod: (period: string) => void;
}> = ({ period, setPeriod }) => {
  const periods = [
    { value: "1month", label: "1 Month" },
    { value: "3months", label: "3 Months" },
    { value: "6months", label: "6 Months" },
    { value: "1year", label: "1 Year" },
    { value: "all", label: "All Time" },
  ];

  return (
    <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => setPeriod(p.value)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
            period === p.value
              ? "bg-primary text-white shadow-lg"
              : "text-gray-400 hover:text-white"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

export const Analytics: React.FC = () => {
  const [period, setPeriod] = useState<string>("all");
  const [currency, setCurrency] = useState<"NGN" | "USD" | "GHS">("NGN");

  const { data, loading, error } = useQuery(GET_ANALYTICS, {
    variables: {
      period: period,
    },
    fetchPolicy: "network-only", // Force network request to ensure fresh data
    notifyOnNetworkStatusChange: true,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        Error loading analytics
      </div>
    );
  }

  const {
    totalCryptoSold,
    totalWalletWithdrawals,
    totalCryptoSoldCount,
    mostTradedCoins,
    mostTradedGiftcards,
    favouriteBrand,
    bestRateEver,
    mostProfitableDay,
  } = data?.getAnalytics || {};

  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr === "NGN" ? "NGN" : curr === "USD" ? "USD" : "GHS",
    }).format(amount);
  };

  const formatCompactNumber = (number: number) => {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(number);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Prepare data for Most Traded Coins chart
  const coinsChartData =
    mostTradedCoins?.map((coin: any) => ({
      name: coin.coin,
      value:
        currency === "NGN"
          ? coin.totalNGN
          : currency === "USD"
          ? coin.totalUSD
          : coin.totalGHS,
      ...coin,
    })) || [];

  // Prepare data for Most Traded Giftcards chart
  const giftcardsChartData =
    mostTradedGiftcards?.map((card: any) => ({
      name: card.cardName,
      value: card.total,
      ...card,
    })) || [];

  // Calculate dynamic height for bar charts (min 300px, or 60px per item)
  const coinsChartHeight = Math.max(300, coinsChartData.length * 60);
  const giftcardsChartHeight = Math.max(300, giftcardsChartData.length * 60);

  return (
    <div className="min-h-screen py-8">
      <Helmet>
        <title>Analytics | Dashboard</title>
        <meta name="description" content="User Analytics" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Analytics Overview
            </h1>
            <p className="text-gray-400 mt-1">
              Track your trading performance and statistics
            </p>
          </div>
          <PeriodFilter period={period} setPeriod={setPeriod} />
        </div>

        {/* Trading Insights Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white">Trading Insights</h2>

          {/* Total Wallet Withdrawals */}
          <div className="bg-black rounded-xl border border-gray-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <AiOutlineDollar className="text-green-500" />
                Total Wallet Withdrawals
              </h2>
              <div className="text-right">
                <p className="text-sm text-gray-400">Total Withdrawn</p>
                <p className="text-2xl font-bold text-green-500">
                  {formatCurrency(totalWalletWithdrawals?.totalNGN || 0, "NGN")}
                </p>
              </div>
            </div>
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={totalWalletWithdrawals?.history || []}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF" }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={30}
                    tickFormatter={formatDate}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `₦${formatCompactNumber(value)}`}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      border: "1px solid #374151",
                      borderRadius: "0.5rem",
                      color: "#F3F4F6",
                    }}
                    itemStyle={{ color: "#F3F4F6" }}
                    labelStyle={{ color: "#9CA3AF" }}
                    cursor={{ stroke: "#374151", strokeWidth: 1 }}
                    formatter={(value: any) => [
                      formatCurrency(value ?? 0, "NGN"),
                      "Amount",
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#00C853"
                    strokeWidth={3}
                    dot={{ fill: "#00C853", strokeWidth: 2 }}
                    activeDot={{ r: 8 }}
                  />
                  <Brush
                    dataKey="date"
                    height={30}
                    stroke="#00C853"
                    fill="#1F2937"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Total Crypto Sold Count */}
          <div className="bg-black rounded-xl border border-gray-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <AiOutlineRise className="text-blue-500" />
                Total Crypto Sold Count
              </h2>
              <div className="text-right">
                <p className="text-sm text-gray-400">Total Transactions</p>
                <p className="text-2xl font-bold text-blue-500">
                  {totalCryptoSoldCount?.totalCount || 0}
                </p>
              </div>
            </div>
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={totalCryptoSoldCount?.history || []}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF" }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={30}
                    tickFormatter={formatDate}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF" }}
                    axisLine={false}
                    tickLine={false}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      border: "1px solid #374151",
                      borderRadius: "0.5rem",
                      color: "#F3F4F6",
                    }}
                    itemStyle={{ color: "#F3F4F6" }}
                    labelStyle={{ color: "#9CA3AF" }}
                    cursor={{ stroke: "#374151", strokeWidth: 1 }}
                    formatter={(value) => [value ?? 0, "Count"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#2775CA"
                    strokeWidth={3}
                    dot={{ fill: "#2775CA", strokeWidth: 2 }}
                    activeDot={{ r: 8 }}
                  />
                  <Brush
                    dataKey="date"
                    height={30}
                    stroke="#2775CA"
                    fill="#1F2937"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Total Crypto Sold History */}
        <div className="bg-black rounded-xl border border-gray-800 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <AiOutlineRise className="text-primary" />
              Total Crypto Sold History
            </h2>
            <div className="text-right">
              <p className="text-sm text-gray-400">Total Volume</p>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(totalCryptoSold?.totalNGN || 0, "NGN")}
              </p>
            </div>
          </div>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={totalCryptoSold?.history || []}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />

                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF" }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={30}
                  tickFormatter={formatDate}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `₦${formatCompactNumber(value)}`}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1F2937",
                    border: "1px solid #374151",
                    borderRadius: "0.5rem",
                    color: "#F3F4F6",
                  }}
                  itemStyle={{ color: "#F3F4F6" }}
                  labelStyle={{ color: "#9CA3AF" }}
                  cursor={{ stroke: "#374151", strokeWidth: 1 }}
                  formatter={(value: any) => [
                    formatCurrency(value ?? 0, "NGN"),
                    "Amount",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#F7931A"
                  strokeWidth={3}
                  dot={{ fill: "#F7931A", strokeWidth: 2 }}
                  activeDot={{ r: 8 }}
                />
                <Brush
                  dataKey="date"
                  height={30}
                  stroke="#F7931A"
                  fill="#1F2937"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Favourite Brand */}
          <div className="bg-black rounded-xl border border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <AiOutlineStar className="text-yellow-500 text-xl" />
              </div>
              <h3 className="text-lg font-bold text-white">Favourite Brand</h3>
            </div>
            {favouriteBrand ? (
              <div>
                <p className="text-2xl font-bold text-white mb-1">
                  {favouriteBrand.cardName}
                </p>
                <p className="text-sm text-gray-400">
                  {favouriteBrand.count} times sold
                </p>
              </div>
            ) : (
              <p className="text-gray-400">No data available</p>
            )}
          </div>

          {/* Best Rate Ever */}
          <div className="bg-black rounded-xl border border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <AiOutlineRise className="text-green-500 text-xl" />
              </div>
              <h3 className="text-lg font-bold text-white">Best Rate Ever</h3>
            </div>
            {bestRateEver ? (
              <div>
                <p className="text-2xl font-bold text-white mb-1">
                  ₦{bestRateEver.rate.toLocaleString()}
                </p>
                <p className="text-sm text-gray-400">{bestRateEver.cardName}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(bestRateEver.date)}
                </p>
              </div>
            ) : (
              <p className="text-gray-400">No data available</p>
            )}
          </div>

          {/* Most Profitable Day */}
          <div className="bg-black rounded-xl border border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <AiOutlineCalendar className="text-primary text-xl" />
              </div>
              <h3 className="text-lg font-bold text-white">
                Most Profitable Day
              </h3>
            </div>
            {mostProfitableDay ? (
              <div>
                <p className="text-2xl font-bold text-white mb-1">
                  {formatCurrency(mostProfitableDay.totalNGN, "NGN")}
                </p>
                <p className="text-sm text-gray-400">
                  {formatDate(mostProfitableDay.date)}
                </p>
              </div>
            ) : (
              <p className="text-gray-400">No data available</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Most Traded Coins */}
          <div className="bg-black rounded-xl border border-gray-800 p-6 flex flex-col max-h-[800px]">
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <AiFillPieChart className="text-blue-500" />
                Most Traded Coins
              </h2>
              <CurrencyToggle currency={currency} setCurrency={setCurrency} />
            </div>
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              <div
                style={{ height: `${coinsChartHeight}px`, minHeight: "300px" }}
                className="w-full mb-6"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={coinsChartData} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      stroke="#9CA3AF"
                      tick={{ fill: "#9CA3AF" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value >= 1000 ? `${value / 1000}k` : value
                      }
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      stroke="#9CA3AF"
                      tick={{ fill: "#9CA3AF" }}
                      axisLine={false}
                      tickLine={false}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1F2937",
                        border: "1px solid #374151",
                        borderRadius: "0.5rem",
                        color: "#F3F4F6",
                      }}
                      itemStyle={{ color: "#F3F4F6" }}
                      labelStyle={{ color: "#9CA3AF" }}
                      cursor={{ fill: "#374151", opacity: 0.4 }}
                      formatter={(value : any) => [
                        formatCurrency(value ?? 0, currency),
                        "Volume",
                      ]}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                      {coinsChartData.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                {mostTradedCoins?.map((coin: any, index: number) => (
                  <div
                    key={coin.coin}
                    className="bg-gray-900 rounded-lg p-4 flex items-center justify-between border border-gray-800 hover:border-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white uppercase border border-gray-700"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length] + "40",
                          borderColor: COLORS[index % COLORS.length],
                        }}
                      >
                        {coin.coin[0]}
                      </div>
                      <div>
                        <h3 className="font-bold text-white capitalize">
                          {coin.coin}
                        </h3>
                        <p className="text-sm text-gray-400">
                          Rank #{index + 1}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white text-lg">
                        {formatCurrency(
                          currency === "NGN"
                            ? coin.totalNGN
                            : currency === "USD"
                            ? coin.totalUSD
                            : coin.totalGHS,
                          currency
                        )}
                      </p>
                      <p className="text-xs text-gray-400 uppercase">
                        {currency} Volume
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Most Traded Giftcards */}
          <div className="bg-black rounded-xl border border-gray-800 p-6 flex flex-col max-h-[800px]">
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <AiOutlineTrophy className="text-yellow-500" />
                Top Giftcards
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              <div
                style={{
                  height: `${giftcardsChartHeight}px`,
                  minHeight: "300px",
                }}
                className="w-full mb-6"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={giftcardsChartData} layout="vertical">
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#374151"
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      stroke="#9CA3AF"
                      tick={{ fill: "#9CA3AF" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value >= 1000 ? `${value / 1000}k` : value
                      }
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      stroke="#9CA3AF"
                      tick={{ fill: "#9CA3AF", fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1F2937",
                        border: "1px solid #374151",
                        borderRadius: "0.5rem",
                        color: "#F3F4F6",
                      }}
                      itemStyle={{ color: "#F3F4F6" }}
                      labelStyle={{ color: "#9CA3AF" }}
                      cursor={{ fill: "#374151", opacity: 0.4 }}
                      formatter={(value : any) => [
                        formatCurrency(value ?? 0, "NGN"),
                        "Volume",
                      ]}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                      {giftcardsChartData.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                {mostTradedGiftcards?.map((card: any, index: number) => (
                  <div
                    key={card.cardName}
                    className="bg-gray-900 rounded-lg p-4 flex items-center justify-between border border-gray-800 hover:border-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold border border-gray-700`}
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-white">
                          {card.cardName}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {card.count} Trades
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white text-lg">
                        {formatCurrency(card.total, "NGN")}
                      </p>
                      <p className="text-xs text-gray-400">Total Volume</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
